/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Fragment, type FunctionComponent } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { getPrettyValueWithDecimals } from '@modules/billing-lib/utils'

import ProgressBar from './ProgressBar'
import { getBalanceColor } from './utils'

type Props = {
  current: number
  max: number
  showPercentage?: boolean
}

const BalanceInfo: FunctionComponent<Props> = ({ current, max, showPercentage }) => {
  const budgetExceeded = current > max
  const statusTextColor = budgetExceeded ? 'danger' : 'subdued'

  const ratio = Math.floor((current / max) * 100 || 0)
  const ratioColor = getBalanceColor(current, max)
  const balanceText = `${getPrettyValueWithDecimals(current, 2)} / ${getPrettyValueWithDecimals(max, 2)}`

  return (
    <EuiFlexGroup direction='column' gutterSize='xs' responsive={false}>
      <EuiFlexGroup direction='row' gutterSize='xs' alignItems='center'>
        {showPercentage && (
          <Fragment>
            <EuiText
              size='s'
              color={ratioColor}
              css={css`
                white-space: 'nowrap';
              `}
            >
              <strong>{ratio}%</strong>
            </EuiText>
            <span>-</span>
          </Fragment>
        )}
        <EuiText
          size='s'
          css={css`
            white-space: 'nowrap';
          `}
        >
          {balanceText}
        </EuiText>
      </EuiFlexGroup>
      <EuiFlexItem>
        <ProgressBar current={current} max={max} />
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText
          size='xs'
          color={statusTextColor}
          css={css`
            white-space: 'nowrap';
          `}
        >
          {budgetExceeded ? (
            <FormattedMessage
              id='billing.notifications.ecu-balance.exceeded'
              defaultMessage='{overage} ECU over budget'
              values={{
                overage: getPrettyValueWithDecimals(current - max),
              }}
            />
          ) : (
            <FormattedMessage
              id='billing.notifications.ecu-balance.available'
              defaultMessage='{availableBalance} ECU remaining'
              values={{
                availableBalance: getPrettyValueWithDecimals(max - current),
              }}
            />
          )}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default BalanceInfo
